// What the sessions' pages need beside the store: the API's runners,
// transcripts and kept diffs (a session's, and a proposal's on the Board),
// and how a transcript is followed (sessions/follow.ts).
import { createContext, useCallback, useContext, useEffect, useInsertionEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react'
import { CheckProgressProvider } from '../checks/progress'
import { ApiError, errorMessage } from '../api/client'
import type { TerminalClientFactory } from '../api/terminal'
import { TerminalClients } from './terminals'
import type { OpenTranscript } from '../api/transcript'
import type { Api, Runner, Session } from '../api/types'
import { useBoardState, useStore } from '../store/context'
import type { BoardStore } from '../store/store'
import { localRunner, localTranscript } from '../api/shellLocalReads'
import { TranscriptFollower, type TranscriptView } from './follow'

export interface SessionsEnv {
  /** Only person-cookie deployments inject the live terminal factory. */
  terminals?: TerminalClientFactory
  api: Pick<Api, 'runners' | 'transcript' | 'sessionChangesDiff' | 'proposalChanges' | 'proposalChangesDiff' | 'agentSettings' | 'setAgentSettings' | 'changeCommandRule' | 'runnerSettings' | 'setRunnerSettings' | 'programTokens' | 'createProgramToken' | 'revokeProgramToken' | 'github' | 'refreshGitHub' | 'repositorySettings'> & Partial<Pick<Api, 'planLimits' | 'checkProgress' | 'cacheHealth' | 'localModels' | 'localModel' | 'queueDetail'>>
  /** Opens a transcript's socket; null, and the pages poll. */
  openTranscript: OpenTranscript | null
  /** How often a transcript is read while no socket brings it. */
  pollMs: number
  /** How long before a transcript's socket opens again, after `attempt` failures in a row. */
  reconnectMs?: (attempt: number) => number
}

const TerminalsContext = createContext<TerminalClients | null>(null)
const SessionsContext = createContext<SessionsEnv | null>(null)

export function SessionsProvider({ env, children }: { env: SessionsEnv; children: ReactNode }) {
  const store = useStore()
  const terminals = useMemo(() => env.terminals ? new TerminalClients(store, env.terminals) : null, [store, env.terminals])
  // Provider lives above workspace Activity. Hiding one page never disposes auth.
  useEffect(() => terminals ? store.subscribe(terminals.refresh) : undefined, [store, terminals])
  useInsertionEffect(() => () => terminals?.dispose(), [terminals])
  return <SessionsContext.Provider value={env}><TerminalsContext.Provider value={terminals}><CheckProgressProvider api={env.api}>{children}</CheckProgressProvider></TerminalsContext.Provider></SessionsContext.Provider>
}

export function useSessionsEnv(): SessionsEnv {
  const env = useContext(SessionsContext)
  if (!env) throw new Error('useSessionsEnv needs a SessionsProvider')
  return env
}

/** Share a session's client across views; release it only on real unmount or
 * scope replacement, not Activity hiding. No render opens a shell. */
export function useTerminalClient(session: string | null) {
  const terminals = useContext(TerminalsContext)
  const {me, status} = useBoardState()
  const client = useMemo(() => session && status !== 'signed_out' ? terminals?.get(session) ?? null : null, [terminals, session, status, me?.workspace.id, me?.member?.id])
  useInsertionEffect(() => client && terminals ? terminals.retain(client) : undefined, [client, terminals])
  return client
}

/** The member the pages act as, as the store names it on its requests; null until it is known. */
function memberOf(store: BoardStore): string | null {
  return store.getState().me?.member?.id ?? null
}

/**
 * After a 401 or a 4401 of the pages' own: whether the same person is still
 * signed in, once the store's last read of who is (`whenSessionKnown`) has
 * decided, not before; a read that a newer one replaced decides nothing.
 */
export async function samePersonOnceKnown(store: BoardStore): Promise<boolean> {
  const before = memberOf(store)
  await store.whenSessionKnown()
  return store.getState().status !== 'signed_out' && memberOf(store) === before
}

/** How many sessions' transcripts stay held, stopped, after their page goes. */
export const KEPT_TRANSCRIPTS = 12

interface KeptTranscript {
  follower: TranscriptFollower
  cache: ReturnType<BoardStore['transcriptCache']>
  /** The mounted pages following it now. */
  users: number
}

/** The followers kept, by sessions environment and store, the most recently used last. */
const kept = new WeakMap<SessionsEnv, WeakMap<BoardStore, Map<string, KeptTranscript>>>()

function keptFor(env: SessionsEnv, store: BoardStore): Map<string, KeptTranscript> {
  let byStore = kept.get(env)
  if (!byStore) kept.set(env, (byStore = new WeakMap()))
  let held = byStore.get(store)
  if (!held) byStore.set(store, (held = new Map()))
  return held
}

/** Forgets the least recently used followers no page follows, past the most kept. */
function evict(held: Map<string, KeptTranscript>): void {
  for (const [key, entry] of held) {
    if (held.size <= KEPT_TRANSCRIPTS) return
    if (entry.users > 0) continue
    entry.follower.stop()
    held.delete(key)
  }
}

/**
 * A session's transcript, followed while the component is mounted, as the
 * member the store acts as, and its socket closed when it goes. After a 401
 * or a 4401, the store reads who is signed in, as for its own requests: the
 * transcript follows again when the same person is, and the page goes
 * otherwise.
 *
 * The last sessions opened stay held, stopped, once their page goes
 * ([`KEPT_TRANSCRIPTS`]): coming back to one draws what it held at once, then
 * reads only what was said since, rather than the whole transcript again.
 */
export function useTranscript(session: Session | null): TranscriptView {
  const env = useSessionsEnv()
  const store = useStore()
  const { me, proposals } = useBoardState()
  const cache = store.transcriptCache()
  const id = session?.id ?? ''
  const ended = session?.state === 'ended'
  /** Whether the session has an open proposal: when it ends, Poulpe writes a notice in the transcript (Poulpe-565). */
  const proposing = id !== '' && [...proposals.values()].some((p) => p.session_id === id)
  const wasProposing = useRef(proposing)
  /** Whether it had ended when the follower was made; what changes after reaches it through `end`. */
  const endedAtFirst = useRef(ended)
  /** Offline, a session of this Mac's runner reads what the runner holds, inside the Mac app (Poulpe-828). */
  const local = !!session && session.runner_id !== '' && session.runner_id === localRunner()
  const key = `${id}\n${me?.workspace.id ?? ''}\n${me?.member?.id ?? ''}\n${local}`
  const follower = useMemo(() => {
    const held = id ? keptFor(env, store) : null
    const known = held?.get(key)
    if (known && known.cache === cache && known.follower.canStart()) return known.follower
    const follower = new TranscriptFollower({
      api: env.api,
      session: id,
      ended: endedAtFirst.current,
      open: env.openTranscript,
      pollMs: env.pollMs,
      reconnectMs: env.reconnectMs,
      actingAs: () => memberOf(store),
      onUnauthorized: () => samePersonOnceKnown(store),
      cache,
      local: local ? (after) => localTranscript(id, after) : null,
    })
    if (held && !(known && known.users > 0)) held.set(key, { follower, cache, users: 0 })
    return follower
  }, [env, id, store, cache, key, local])
  useEffect(() => {
    if (!id) return
    const held = keptFor(env, store)
    const entry = held.get(key)
    const ours = entry?.follower === follower ? entry : null
    if (ours) {
      held.delete(key)
      held.set(key, ours)
      ours.users++
    }
    follower.start()
    return () => {
      if (ours) ours.users--
      if (!ours || ours.users === 0) follower.stop()
      evict(held)
    }
  }, [env, store, key, follower, id])
  useEffect(() => {
    if (ended) follower.end()
    else follower.resume()
  }, [follower, ended])
  useEffect(() => {
    // An ended session's transcript is not followed: read its proposal's notice once it ends.
    if (wasProposing.current && !proposing) follower.proposalEnded()
    wasProposing.current = proposing
  }, [follower, proposing])
  return useSyncExternalStore(follower.subscribe, follower.getState)
}

export interface RunnersView {
  /** Null until read. */
  runners: Runner[] | null
  loading: boolean
  /** Why the last read failed; null when it did not. */
  error: string | null
  /** Read them again; what was read stays until then. */
  reload: () => void
}

/** The workspace's runners, read when the component mounts and when asked. A 401 has the store read who is signed in, unless it is reading already. */
export function useRunners(): RunnersView {
  const { api } = useSessionsEnv()
  const store = useStore()
  const [runners, setRunners] = useState<Runner[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [asked, setAsked] = useState(0)
  useEffect(() => {
    let current = true
    setLoading(true)
    api.runners(memberOf(store)).then(
      (read) => {
        if (!current) return
        setRunners(Array.isArray(read) ? read : [])
        setError(null)
        setLoading(false)
      },
      (err: unknown) => {
        if (!current) return
        setError(errorMessage(err))
        setLoading(false)
        if (err instanceof ApiError && err.status === 401) void store.whenSessionKnown()
      },
    )
    return () => {
      current = false
    }
  }, [api, store, asked])
  const reload = useCallback(() => setAsked((n) => n + 1), [])
  return { runners, loading, error, reload }
}
